import type { AssignedMember } from '@indipia/shared-features';
import axios from 'axios';
import { apiClient } from '@indipia/shared/api/client';
import { useAuthStore } from '@indipia/shared/stores/authStore';
import { API_PRX } from './endpoints';
import type { AxiosProgressEvent } from 'axios';
import type { ExternalLinkSendPayload, ExternalLinkSendResult } from '@indipia/shared-features';

// EventSource(SSE)·핸드오프 게스트 호출은 axios 인스턴스를 못 쓰거나(SSE) apiClient를 쓰면 안 되므로
// (아래 handoffClient 주석 참고) baseURL을 직접 구성한다 (client.ts와 동일 규칙).
const SSE_API_BASE = (import.meta.env.VITE_API_BASE || '') + '/api';

// QR 핸드오프 claim·upload 전용 — apiClient(회원 로그인 토큰 자동첨부 인터셉터)를 쓰면 안 된다.
// 로그아웃 상태 폰: 회원 토큰이 없어 Authorization 미첨부 → FlowGate 401(claimHandoff는 이걸 "QR 만료"로 오인시켰던
// 실제 원인). 로그인 상태 폰: 그 브라우저에 로그인된 회원 토큰이 URL의 게스트 토큰을 덮어써 엉뚱한 신원으로 요청됨.
// 이 두 엔드포인트는 URL의 handoff token만을 Authorization으로 명시 전송해야 한다.
const handoffClient = axios.create({ baseURL: SSE_API_BASE });

interface ApiResponse<T> {
  data: T;
}

export interface CommonCodeItem {
  code: string;
  codeName: string;
  codeOrder: number | null;
}

export type CommonCodesResponse = Record<string, CommonCodeItem[]>;

const PHOTO_COMMON_CODE_GROUPS = [
  'A01_SPACE_TAG',
] as const;

export interface UploadResponse {
  photoId: number;
  fileId: string | null;
  classifyStatus: string;
  takenDtm: string;
  thumbUrl: string | null;
}

export interface ClassifyMatchResult {
  photoId: number;
  matchedSiteId: number | null;
  siteName: string | null;
  address?: string | null;
  distanceM: number | null;
}

export interface ClassifyResponse {
  totalCount: number;
  matchedCount: number;
  unmatchedCount: number;
  results: ClassifyMatchResult[];
}


export interface UnclassifiedItem {
  photoId: number;
  thumbUrl: string | null;
  takenDtm: string | null;
  gpsLat: number | null;
  gpsLng: number | null;
  sessionId: number | null;
  address?: string | null;
  createdAt?: string | null;
  matchReason?: string | null;  // SERVICE_ERROR 만 재매칭 대상
}

export interface UnclassifiedResponse {
  totalCount: number;
  todayUploadCount: number;
  lastUploadedAt: string | null;
  items: UnclassifiedItem[];
}

export interface SitePhotoItem {
  photoId: number;
  thumbUrl: string | null;
  takenAt: string | null;
  registeredAt?: string | null;
  gpsLat?: number | null;
  address?: string | null;
  sessionId?: number | null;
}

export interface TagEditSiteGroup {
  siteId: number;
  siteName: string;
  totalOnSite: number;
  thumbPhotos: SitePhotoItem[];
  sessionPhotoIds: number[];
}

export interface SitePhotoPageResponse {
  totalCount: number;
  hasNext: boolean;
  photos: SitePhotoItem[];
}

export interface SiteListItem {
  siteId: number;
  siteName: string;
  address: string | null;
  customerName: string | null;
  siteStatus: string | null;
  siteStatusName: string | null;
  photoCount: number;
  unassignedCount: number;
  todayUploadCount: number;
  lastUploadedAt: string | null;
  thumbnails: Array<{ photoId: number; url: string; takenAt: string | null }>;
  /** 현장 담당자·관리자 — 배정 순서대로 (2026-10-12 개선사항 D-4) */
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
}

export interface SiteListResponse {
  totalSiteCount: number;
  totalPhotoCount: number;
  totalUnassignedCount: number;
  limit: number;
  offset: number;
  sites: SiteListItem[];
  /** 상태별 현장 수(담당자 조건 반영) — 상단 요약·상태 칩 건수용(P-1③) */
  statusCounts?: Record<string, number>;
}

export interface CustomerSiteItem {
  siteId: number;
  customerId: number | null;
  customerName: string | null;
  siteName: string;
  siteAddress: string | null;
  siteStatus: string | null;
  siteStatusName: string | null;
}

export interface CustomerSiteListResponse {
  items: CustomerSiteItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface SiteDetailCard {
  siteId: number;
  siteName: string;
  siteStatus: string | null;
  siteStatusName: string | null;
  customerName: string | null;
  customerMobile: string | null;
  address: string | null;
  photoCount: number;
  spaceTagCount: number;
  untaggedPhotoCount: number;
  /** 현장 담당자·관리자 (P-3) */
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
}

export interface PanelSiteListItem {
  siteId: number;
  siteName: string | null;
  customerName: string | null;
  address: string | null;
  bldgName: string | null;
  dong: string | null;
  ho: string | null;
  /** 생성 시 서버가 사업자 단위로 순환 배정한 hex 색상. 이 필드 도입 이전 생성 현장은 null */
  siteColor: string | null;
  siteStatus: string;
  siteStatusName?: string | null;
  /** 현장 담당자·관리자 (P-2) */
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
  planStatus: string;
  createdAt: string;
  photoCount: number;
  lastUploadedAt: string | null;
}

export interface PanelSiteListResponse {
  totalCount: number;
  limit: number;
  offset: number;
  items: PanelSiteListItem[];
}

export interface CheckDuplicateResult {
  photoId: number | null;
  matchedSiteId: number | null;
  siteName: string | null;
  hasGps: boolean;
  address: string | null;
}

export interface CheckDuplicatesResponse {
  results: CheckDuplicateResult[];
  matchedCount: number;
  unmatchedCount: number;
}

export interface BatchUploadPhotoItem {
  photoId: number;
  fileId: string | null;
  takenAt: string | null;
  registeredAt?: string | null;
  spaceTag: string | null;
  thumbUrl: string | null;
  source: string | null;
  visitNo: number | null;
  siteId: number | null;
  isBlurry: boolean;
}

export interface BatchUploadFailure {
  fileName: string;
  reason: string | null;
}

export interface PhotoTagItem {
  tagId: number;
  tagType: string;
  tagCode: string | null;
  tagValue: string;
  tagGenerator: string;
}

export interface BatchUploadResponse {
  totalCount: number;
  photos: BatchUploadPhotoItem[];
  sessionId: number | null;   // U3 — 업로드가 생성한 세션 id (사전 세션생성 대체)
  failures: BatchUploadFailure[];  // 저장 실패한 장(부분 실패). 이 장만 재시도한다. 전량 성공 시 빈 배열
  // 이번 배치에서 덮어쓴 사진(photoId, JSON 키라 string)의 기존 태그. 덮어쓰기는 photoId를 재사용해
  // 기존 태그가 그대로 남아있는데 photos[]엔 태그 필드가 없어 이걸로 보강한다. 덮어쓴 건 없으면 빈 객체.
  overwrittenTags: Record<string, PhotoTagItem[]>;
}

export interface RematchResponse {
  matched: number;      // 이번에 현장 배정된 장
  stillError: number;   // 여전히 매칭서비스 실패
  unmatched: number;    // 서비스는 응답했으나 근처 현장 없음(재시도 대상 아님)
}

export interface AssignSiteResponse {
  updatedCount: number;
  siteId: number | null;
  addresses: Record<string, string>;  // 미분류 복귀(siteId=null) 시 lazy 역지오코딩된 주소. photoId(string) → address
}

// 분류화면 신규현장 등록 시 미분류 흡수(Photo 현장등록 미분류흡수 설계 v1.2) 응답
export interface AbsorbResponse {
  absorbedPhotoIds: number[];
  count: number;
}

/** sv-photo SiteDto.CreateSiteRequest */
export interface CreateSiteRequest {
  customerId: number | null;
  siteName: string;
  address: string;
  addressDtl?: string | null;
  gpsLat?: number | null;
  gpsLng?: number | null;
  /** 현장 담당자·관리자(P-5). 담당자 미전송이면 등록자 본인 */
  chargeMemberIds?: number[];
  managerMemberIds?: number[];
}

export interface SearchPhotoItem {
  photoId: number;
  takenAt: string | null;
  registeredAt?: string | null;
  thumbUrl: string | null;
  spaceTag: string | null;
  hasGpsError: boolean;
  tags: Array<{ tagId: number; tagType: string; tagValue: string; tagCode: string | null; tagGenerator: string }>;
}

export interface SearchSiteGroup {
  siteId: number;
  siteName: string;
  address: string | null;
  customerName: string | null;
  photoCount: number;
  photos: SearchPhotoItem[];
  /** 현장 담당자·관리자 — 묶음 머리글 배지 (P-4) */
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
}

export interface SearchGroupedResponse {
  totalCount: number;
  totalSiteCount: number;
  siteCount: number;
  siteOffset: number;
  siteLimit: number;
  sites: SearchSiteGroup[];
}

export interface PhotoDetailResponse {
  photoId: number;
  originalUrl: string | null;
  thumbUrl: string | null;
  takenAt: string | null;
  originalFileName: string | null;
  width: number | null;
  height: number | null;
  fileSize: number | null;
  gpsLat: number | null;
  gpsLng: number | null;
  siteName: string | null;
  siteId: number | null;
  siteAddress: string | null;
  memo: string | null;
  tags: Array<{ tagType: string; tagValue: string; tagCode: string | null }>;
}

export interface ShareHistoryPhotoItem {
  photoId: number;
  thumbUrl: string | null;
  takenAt: string | null;
  registeredAt?: string | null;
}

export interface SitePreviewGroup {
  siteId: number;
  siteName: string;
  photos: ShareHistoryPhotoItem[];
}

export interface ShareHistoryRecipient {
  /** 이력 행 번호 — 발송 한 건의 키(서버 미배포 구간에는 없을 수 있음) */
  historyId?: number | null;
  shareId: number | null;
  recipientName: string;
  recipientType: string;
  shareUrl: string | null;
  shareRecipient: string | null;
  shareChannel: string | null;
  /** 발송 상태(ZEU_SEND_STATUS 코드) */
  sendStatus: string | null;
  expiresDtm: string | null;
  revokedYn: string | null;
  viewedDtm: string | null;
  /** 재발송 가능 여부 — 서버 판정(수신자 행 단위, ShareBundleService.resendBlockedReason) */
  resendable: boolean;
  /** 불가 사유 NOT_SUPPORTED·REVOKED·EXPIRED·SEND_WINDOW_CLOSED, 가능하면 null */
  resendBlockedReason: string | null;
}

export interface ShareHistoryGroup {
  groupKey: string;
  bundleId: string;
  msgTitle: string | null;
  sentDtm: string;
  photoCount: number;
  photosBySite: SitePreviewGroup[];
  recipients: ShareHistoryRecipient[];
}

// 서버 PageResponse — GET /share-bundles/history 는 page(0부터)·size(기본 20) 로 잘라 내려준다.
export interface ShareHistoryListResponse {
  items: ShareHistoryGroup[];
  totalCount: number;
  hasNext: boolean;
}

export interface BundleSharePhoto {
  photoId: number;
  fileId: string | null;
  takenAt: string | null;
  registeredAt: string | null;
  spaceTag: string | null;
  thumbUrl: string | null;
  originalUrl: string | null;
  siteId: number | null;
  isBlurry: boolean | null;
}

export interface BundleShareViewData {
  bundleId: string;
  siteId: number | null;
  sites: Array<{ siteId: number; siteName: string | null }>;
  msgTitle: string | null;
  msgContent: string | null;
  sentDtm: string | null;
  expiresDtm: string | null;
  senderName: string | null;
  totalCount: number;
  photos: BundleSharePhoto[];
}


export interface HandoffIssueResponse {
  token: string;
  sessionId: number;
  deepLinkUrl: string;
  expiresIn: number;
}

export interface HandoffClaimResponse {
  claimed: boolean;
  sessionId: number;
}

export const photoApi = {
  uploadPhoto(
    file: File,
    sessionId: number,
    onProgress?: (pct: number) => void,
  ) {
    const form = new FormData();
    form.append('file', file);
    form.append('sessionId', String(sessionId));
    return apiClient.post<ApiResponse<UploadResponse>>(
      `${API_PRX.PHOTO}/photos/upload`,
      form,
      {
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (e.total) onProgress?.(Math.round((e.loaded / e.total) * 100));
        },
      },
    );
  },

  checkDuplicates(fileHashes: string[]) {
    return apiClient.post<ApiResponse<CheckDuplicatesResponse>>(
      `${API_PRX.PHOTO}/photos/check-duplicates`,
      { file_hashes: fileHashes },
    );
  },

  uploadBatch(
    files: File[],
    options?: { sessionId?: number; siteId?: number; source?: string; gpsLat?: number; gpsLng?: number; overwrite?: boolean },
    onProgress?: (pct: number) => void,
  ) {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    if (options?.sessionId  != null) form.append('sessionId', String(options.sessionId));
    if (options?.siteId     != null) form.append('siteId',    String(options.siteId));
    if (options?.source)             form.append('source',    options.source);
    if (options?.gpsLat     != null) form.append('gpsLat',    String(options.gpsLat));
    if (options?.gpsLng     != null) form.append('gpsLng',    String(options.gpsLng));
    if (options?.overwrite)          form.append('overwrite', 'true');
    return apiClient.post<ApiResponse<BatchUploadResponse>>(
      `${API_PRX.PHOTO}/photos/upload/batch`,
      form,
      {
        timeout: 30000,
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (e.total) onProgress?.(Math.round((e.loaded / e.total) * 100));
        },
      },
    );
  },

  classifyPhotos(photoIds: number[]) {
    return apiClient.post<ApiResponse<ClassifyResponse>>(
      `${API_PRX.PHOTO}/photos/classify`,
      { photoIds },
    );
  },

  // 매칭서비스 실패(SERVICE_ERROR)로 미분류에 남은 사진 다시 매칭
  rematch() {
    return apiClient.post<ApiResponse<RematchResponse>>(
      `${API_PRX.PHOTO}/photos/rematch`,
      null,
    );
  },

  getUnclassifiedPhotos(limit = 50, offset = 0, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<UnclassifiedResponse>>(
      `${API_PRX.PHOTO}/photos/unclassified`,
      { params: { limit, offset }, signal },
    );
  },

  getSitePhotos(siteId: number, limit = 4, offset = 0) {
    return apiClient.get<ApiResponse<SitePhotoPageResponse>>(
      `${API_PRX.PHOTO}/sites/${siteId}/photos`,
      { params: { limit, offset } },
    );
  },

  getSiteList(
    params?: { limit?: number; offset?: number; keyword?: string; siteStatus?: string; startDate?: string; endDate?: string; sort?: 'lastUploadedAt' | 'siteName'; memberIds?: number[] },
    signal?: AbortSignal,
  ) {
    const query: Record<string, string | number> = {};
    // 담당자·관리자 조건(D-4) — 빈 배열이면 보내지 않는다(= 전체 현장)
    if (params?.memberIds?.length) query.memberIds = params.memberIds.join(',');
    if (params?.limit    != null) query.limit     = params.limit;
    if (params?.offset   != null) query.offset    = params.offset;
    if (params?.keyword)          query.keyword   = params.keyword;
    if (params?.siteStatus)       query.siteStatus = params.siteStatus;
    if (params?.startDate)        query.startDate = params.startDate;
    if (params?.endDate)          query.endDate   = params.endDate;
    // 기본값은 최신 업로드일 내림차순 — 'siteName'을 넘기면 BE가 이름(가나다)순으로 정렬해서 내려준다
    if (params?.sort === 'siteName') query.sort = params.sort;
    return apiClient.get<ApiResponse<SiteListResponse>>(
      `${API_PRX.PHOTO}/sites`,
      { params: Object.keys(query).length ? query : undefined, signal },
    );
  },

  createSite(data: CreateSiteRequest) {
    return apiClient.post<ApiResponse<{ siteId: number; siteName: string; address: string }>>(
      `${API_PRX.PHOTO}/sites`,
      data,
    );
  },

  searchCustomerSites(keyword: string, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<CustomerSiteListResponse>>(
      `${API_PRX.PHOTO}/customer/sites`,
      { params: { keyword }, signal },
    );
  },

  // 현장 단건 상세 — siteId를 이미 아는 상태에서 전체목록 조회+find 대신 이걸 쓴다 (P-F1).
  getSiteDetail(siteId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<SiteDetailCard>>(
      `${API_PRX.PHOTO}/sites/${siteId}/detail`,
      { signal },
    );
  },

  getPanelSiteList(
    params?: {
      keyword?: string;
      dateFrom?: string;
      dateTo?: string;
      siteStatus?: string;
      /** 담당자·관리자 조건(D-6) */
      memberIds?: number[];
      /** 조건과 무관하게 항상 포함할 현장 — 지금 보고 있는 현장(D-6) */
      includeSiteId?: number;
    },
    signal?: AbortSignal,
  ) {
    const query: Record<string, string> = {};
    if (params?.memberIds?.length) query.memberIds = params.memberIds.join(',');
    if (params?.includeSiteId != null) query.includeSiteId = String(params.includeSiteId);
    if (params?.keyword)    query.keyword    = params.keyword;
    if (params?.dateFrom)   query.dateFrom   = params.dateFrom;
    if (params?.dateTo)     query.dateTo     = params.dateTo;
    if (params?.siteStatus) query.siteStatus = params.siteStatus;
    return apiClient.get<ApiResponse<PanelSiteListResponse>>(
      `${API_PRX.PHOTO}/sites/panel`,
      { params: Object.keys(query).length ? query : undefined, signal },
    );
  },

  // 분류화면 신규현장 등록 직후 호출 — 반경 내 미분류 사진을 이 현장으로 흡수
  absorbPhotos(siteId: number) {
    return apiClient.post<ApiResponse<AbsorbResponse>>(
      `${API_PRX.PHOTO}/photos/absorb`,
      { siteId },
    );
  },

  assignSite(assignments: { photoId: number; siteId: number | null }[]) {
    return apiClient.patch<ApiResponse<AssignSiteResponse>>(
      `${API_PRX.PHOTO}/photos/assign-site`,
      {
        photo_ids: assignments.map(a => a.photoId),
        site_id: assignments[0]?.siteId ?? null,
      },
    );
  },

  deletePhoto(photoId: number) {
    return apiClient.delete<ApiResponse<void>>(
      `${API_PRX.PHOTO}/photos/${photoId}`,
    );
  },

  getPhotoDetail(photoId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<PhotoDetailResponse>>(
      `${API_PRX.PHOTO}/photos/${photoId}`,
      { signal },
    );
  },

  savePhotoMemo(photoId: number, memo: string) {
    return apiClient.patch<ApiResponse<void>>(
      `${API_PRX.PHOTO}/photos/${photoId}/memo`,
      { memo },
    );
  },

  searchPhotos(params: {
    keyword?: string;
    space?: string;
    source?: string;
    startDate?: string;
    endDate?: string;
    offset?: number;
    limit?: number;
    /** 담당자·관리자 조건(D-7) */
    memberIds?: number[];
  }, signal?: AbortSignal) {
    const query: Record<string, string | number> = {};
    if (params.memberIds?.length) query.memberIds = params.memberIds.join(',');
    if (params.keyword)        query.keyword   = params.keyword;
    if (params.space)          query.space     = params.space;
    if (params.source)         query.source    = params.source;
    if (params.startDate)      query.startDate = params.startDate;
    if (params.endDate)        query.endDate   = params.endDate;
    if (params.offset != null) query.offset    = params.offset;
    if (params.limit  != null) query.limit     = params.limit;
    return apiClient.get<ApiResponse<SearchGroupedResponse>>(
      `${API_PRX.PHOTO}/photos/search`,
      { params: query, signal },
    );
  },

  getCommonCodes(signal?: AbortSignal) {
    return apiClient.post<ApiResponse<CommonCodesResponse>>(
      `${API_PRX.PHOTO}/common-codes`,
      { code_group_codes: PHOTO_COMMON_CODE_GROUPS },
      { signal },
    );
  },

  getUserTags(signal?: AbortSignal) {
    return apiClient.get<ApiResponse<{ tags: { userTagId: number; tagValue: string }[] }>>(
      `${API_PRX.PHOTO}/users/me/tags`,
      { signal },
    );
  },

  patchPhotoTags(photoId: number, tags: { tagType: string; tagValue: string; tagCode?: string | null }[]) {
    return apiClient.patch<ApiResponse<{ photoId: number; updated: boolean; updatedAt: string }>>(
      `${API_PRX.PHOTO}/photos/${photoId}/tags`,
      { tags: tags.map(t => ({ tag_type: t.tagType, tag_value: t.tagValue, tag_code: t.tagCode ?? undefined })) },
    );
  },

  saveUserTags(tags: string[]) {
    return apiClient.post<ApiResponse<{ saved: boolean; count: number }>>(
      `${API_PRX.PHOTO}/users/me/tags`,
      { tags },
    );
  },

  getThumb(url: string, signal?: AbortSignal, previewToken?: string) {
    return apiClient.get<Blob>(url, {
      responseType: 'blob',
      signal,
      // 미리보기 화면의 썸네일/원본 이미지도 같은 로그인 토큰이 필요하다 —
      // getBundleViewByShareId와 동일한 이유.
      headers: previewToken ? { Authorization: `Bearer ${previewToken}` } : undefined,
    });
  },

  createShareBundle(
    siteId: number | null,
    siteName: string | null,
    photos: Array<{ photoId: number; siteId: number | null; siteName: string | null }>,
  ) {
    return apiClient.post<ApiResponse<{ bundleId: string }>>(
      `${API_PRX.PHOTO}/share-bundles`,
      { siteId, siteName, photos },
    );
  },

  revokeShare(shareId: number) {
    return apiClient.post<ApiResponse<void>>(
      `${API_PRX.PHOTO}/share-bundles/${shareId}/revoke`,
      null,
    );
  },

  revokeShareBatch(shareBatchId: string) {
    return apiClient.post<ApiResponse<void>>(
      `${API_PRX.PHOTO}/share-bundles/batch/${shareBatchId}/revoke`,
      null,
    );
  },

  // 사진 공유 발송 — 10-Indipia god-BFF가 아니라 sv-photo가 직접 오케스트레이션한다(포토발송이관 v1.1 W6b).
  // useExternalLink 훅에 sendOverride로 주입된다. payload·반환은 공용 발송과 동일 형태(channel 없음 — D4).
  async sendPhotoShare(payload: ExternalLinkSendPayload): Promise<ExternalLinkSendResult> {
    // 고정 BE 계약(POST /photo/shares/send)에 맞춰 body 구성 — sourceAgent·channel 미포함.
    // recipients.mobile은 훅(toApiRecipient)에서 이미 하이픈 제거된 순수 숫자다.
    const body = {
      sourceService: payload.sourceService,
      resourceType:  payload.resourceType,
      resourceRef:   payload.resourceRef,
      shareType:     payload.shareType,
      shareScope:    payload.shareScope,
      authType:      payload.authType,
      expiresAt:     payload.expiresAt,
      variables:     payload.variables,
      ...(payload.dispatchBatchId && { dispatchBatchId: payload.dispatchBatchId }),
      recipients:    payload.recipients,
      ...(payload.buttons && payload.buttons.length > 0 && { buttons: payload.buttons }),
    };
    const res = await apiClient.post<ApiResponse<ExternalLinkSendResult>>(
      `${API_PRX.PHOTO}/shares/send`,
      body,
    );
    const data = res.data.data;
    return {
      shareBatchId:    data.shareBatchId,
      dispatchBatchId: data.dispatchBatchId,
      sentCount:       data.sentCount,
      failedCount:     data.failedCount,
      results:         data.results,
    };
  },

  // 서버는 page·size(기본 20)로 잘라 주지만 이력 화면은 전량을 받아 클라이언트에서 정렬·필터·청크하므로,
  // 큰 size 로 hasNext 가 false 가 될 때까지 이어서 받아 합친다(안전 상한 MAX_PAGES).
  async getShareHistory(params?: { startDate?: string; endDate?: string; keyword?: string }): Promise<ShareHistoryGroup[]> {
    const PAGE_SIZE = 200;
    const MAX_PAGES = 50;
    const base: Record<string, string | number> = { size: PAGE_SIZE, sort: '-sentDtm' };
    if (params?.startDate) base.startDate = params.startDate;
    if (params?.endDate)   base.endDate   = params.endDate;
    if (params?.keyword)   base.keyword   = params.keyword;
    const all: ShareHistoryGroup[] = [];
    for (let page = 0; page < MAX_PAGES; page++) {
      const res = await apiClient.get<ApiResponse<ShareHistoryListResponse>>(
        `${API_PRX.PHOTO}/share-bundles/history`,
        { params: { ...base, page } },
      );
      const d = res.data.data;
      all.push(...d.items);
      if (!d.hasNext || d.items.length === 0) break;
    }
    return all;
  },

  getBundleViewByShareId(shareId: number, preview?: boolean, previewToken?: string, historyId?: number) {
    return apiClient.get<ApiResponse<BundleShareViewData>>(
      `${API_PRX.PHOTO}/share-bundles/by-share/${shareId}/view`,
      {
        params: preview || historyId !== undefined ? { ...(preview ? { preview: true } : {}), ...(historyId !== undefined ? { historyId } : {}) } : undefined,
        // 미리보기(preview)는 ShareGate 없이 열리는 발송자 전용 화면이라, 로그인
        // 토큰이 필요하다. 앱이 URL 프래그먼트로 실어준 토큰을 이 요청 하나에만
        // 명시적으로 붙인다 — apiClient의 기본 인터셉터가 붙이는(이 브라우저
        // 세션 자체의, 외부 브라우저라 대개 없는) 토큰에 의존하지 않기 위해서다.
        headers: previewToken ? { Authorization: `Bearer ${previewToken}` } : undefined,
      },
    );
  },

  // ── QR 핸드오프(휴대폰으로 올리기) ──
  // PC 화면에서 세션을 발급/재발급한다. sessionId를 넘기면 같은 세션으로 재발급(배치 유지) —
  // QR 3분 미스캔 만료 후 재발급 시 사용. apiClient가 로그인 토큰을 자동으로 붙인다.
  // sessionId는 쿼리 파라미터로 보낸다(바디 없음) — axios가 빈 바디 POST에도
  // 기본 Content-Type(x-www-form-urlencoded)을 남겨 서버 @RequestBody 파싱이
  // HttpMediaTypeNotSupportedException으로 막히는 문제를 원천적으로 피한다.
  // siteId 지정 시 폰 QR 업로드가 GPS 매칭 없이 그 현장으로 바로 배정된다 (PC 업로드 화면의 현장 미리 선택과 동일 규칙)
  issueHandoff(sessionId?: number, siteId?: number) {
    const params: Record<string, number> = {};
    if (sessionId != null) params.sessionId = sessionId;
    if (siteId    != null) params.siteId    = siteId;
    return apiClient.post<ApiResponse<HandoffIssueResponse>>(
      `${API_PRX.PHOTO}/photos/handoff`,
      null,
      { params: Object.keys(params).length ? params : undefined },
    );
  },

  // 세션 단위로 도착한 사진 목록 조회 — 기존 배치 응답과 동일 형태(photos/totalCount). SSE 끊김 시 폴링 폴백용.
  listBySession(sessionId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<BatchUploadResponse>>(
      `${API_PRX.PHOTO}/photos`,
      { params: { sessionId }, signal },
    );
  },

  // 휴대폰(모바일 공개 페이지) 진입 즉시 호출 — 게스트 JWT 인증. 첫 claim 승리(1디바이스),
  // 재claim은 409, 3분 미스캔 지난 토큰은 401(HANDOFF_TOKEN_INVALID).
  claimHandoff(token: string) {
    return handoffClient.post<ApiResponse<HandoffClaimResponse>>(
      `${API_PRX.PHOTO}/photos/handoff/${token}/claim`,
      null,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  },

  // 휴대폰(모바일 공개 페이지)에서 호출 — 게스트 JWT 인증. claim 후 업로드 창(10분) 내에만 성공.
  handoffUpload(token: string, files: File[], source?: string) {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    if (source) form.append('source', source);
    return handoffClient.post<ApiResponse<BatchUploadResponse>>(
      `${API_PRX.PHOTO}/photos/handoff/${token}/upload`,
      form,
      { timeout: 30000, headers: { Authorization: `Bearer ${token}` } },
    );
  },

  // 웹(PC)이 세션 S의 실시간 이벤트(PHONE_CONNECTED·PHOTO_ARRIVED)를 구독 — C-04 §9.2.
  // EventSource는 커스텀 헤더를 못 실으므로 access token을 query param으로 전달(FlowGate SseTokenHeaderFilter가 검증).
  // 경로는 표준 SSE 규약 `/api/{service}/sse/{리소스}`(C-04 §9.1·F-03 §7.2)를 따라야 한다 — 이 패턴을
  // 벗어나면 FlowGate가 query param 토큰을 헤더로 승격하지 않아 401이고, nginx의 proxy_buffering off도 안 걸린다.
  subscribeHandoffEvents(sessionId: number): EventSource {
    const token = useAuthStore.getState().accessToken ?? '';
    const url = `${SSE_API_BASE}${API_PRX.PHOTO}/sse/handoff/${sessionId}?token=${encodeURIComponent(token)}`;
    return new EventSource(url);
  },

  // QR 완전 핸드오프 — 회원 단위 채널 구독(HANDOFF_STARTED). 전환 설계 v1.0 §7-2.
  // QR이 무상태 고정 딥링크라 "어느 화면의 QR인지"를 서버가 알 수 없어, 세션이 아니라 회원으로 방송받는다.
  // 폰이 PC와 다른 계정으로 로그인했으면 닿지 않으며, 그때는 패널의 표시 타이머가 폴백이다.
  // 경로 규약은 위 subscribeHandoffEvents와 동일한 이유로 /sse/** 를 지켜야 한다.
  subscribeHandoffMemberEvents(): EventSource {
    const token = useAuthStore.getState().accessToken ?? '';
    const url = `${SSE_API_BASE}${API_PRX.PHOTO}/sse/handoff/member?token=${encodeURIComponent(token)}`;
    return new EventSource(url);
  },

};
